import { useEffect, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { logsApi } from '../api';
import type { LogEntry, LogQuery } from '../api/logs';

// Cloud Logging 읽기 한도가 프로젝트당 분당 60회라 실시간 보기는 10초 간격으로만 묻는다.
const LIVE_INTERVAL_MS = 10_000;

export const useLogStatus = () => {
  return useQuery({
    queryKey: ['logs', 'status'],
    queryFn: () => logsApi.status(),
    staleTime: 60_000,
  });
};

export const useLogList = (query: LogQuery, enabled: boolean) => {
  return useInfiniteQuery({
    queryKey: ['logs', 'list', query],
    queryFn: ({ pageParam }) => logsApi.list({ ...query, page_token: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_page_token ?? undefined,
    enabled,
    refetchOnWindowFocus: false,
  });
};

/**
 * 실시간 보기: newestTimestamp 이후 로그만 주기적으로 묻고 앞에 붙일 것을 돌려준다.
 * 조건(query)이 바뀌면 비운다.
 */
export const useLiveLogs = (query: LogQuery, newestTimestamp: string | undefined, active: boolean) => {
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [error, setError] = useState<unknown>(null);
  const queryKey = JSON.stringify(query);

  useEffect(() => {
    setEntries([]);
    setError(null);
  }, [queryKey]);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    let after = entries[0]?.timestamp ?? newestTimestamp;

    const tick = async () => {
      try {
        const res = await logsApi.list({ ...query, after, page_size: 200 });
        if (cancelled || res.entries.length === 0) return;
        after = res.entries[0].timestamp;
        setEntries((prev) => {
          const seen = new Set(prev.map((e) => e.insert_id));
          return [...res.entries.filter((e) => !seen.has(e.insert_id)), ...prev];
        });
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e);
      }
    };

    const id = window.setInterval(tick, LIVE_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
    // entries 는 시작 시각만 정하는 데 쓴다 — 매 틱마다 interval 을 다시 만들지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, queryKey, newestTimestamp]);

  return { entries, error };
};
